import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { SEOScore } from '../lib/seoScoreCalculator';

interface UseSEOScoreResult {
  seoScore: SEOScore | null;
  loading: boolean;
  error: string | null;
  refreshScore: () => Promise<void>;
}

export function useSEOScore(websiteId: string | null, enabled: boolean = true): UseSEOScoreResult {
  const [seoScore, setSeoScore] = useState<SEOScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSEOScore = async () => {
    if (!websiteId || !enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, try to get the latest stored SEO score
      const { data: storedScore, error: fetchError } = await supabase
        .from('seo_scores')
        .select('*')
        .eq('website_id', websiteId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = no rows found
        throw fetchError;
      }

      // If we have a recent score (within last 7 days), use it
      if (storedScore) {
        const lastUpdated = new Date(storedScore.last_updated);
        const daysSinceUpdate = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysSinceUpdate < 7) {
          setSeoScore({
            overall: storedScore.overall_score,
            breakdown: storedScore.breakdown,
            grade: storedScore.grade,
            insights: storedScore.insights || [],
            improvements: storedScore.improvements || [],
            lastUpdated: storedScore.last_updated,
            dataQuality: storedScore.data_quality
          });
          setLoading(false);
          return;
        }
      }

      // If no recent score, calculate a new one
      const { data: seoScoreData, error: calculateError } = await supabase.functions.invoke('calculate-seo-score', {
        body: {
          websiteId,
          userId: (await supabase.auth.getUser()).data.user?.id
        }
      });

      if (calculateError) {
        throw calculateError;
      }

      if (seoScoreData?.seoScore) {
        setSeoScore(seoScoreData.seoScore);
      } else {
        throw new Error('Failed to calculate SEO score');
      }

    } catch (err) {
      console.error('Error fetching SEO score:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch SEO score');
      
      // Set a fallback score if we can't calculate one
      setSeoScore({
        overall: 0,
        breakdown: {
          visibility: { score: 0, weight: 0.35 },
          engagement: { score: 0, weight: 0.25 },
          performance: { score: 0, weight: 0.20 },
          technical: { score: 0, weight: 0.20 }
        },
        grade: 'F',
        insights: ['Unable to calculate SEO score - please check your data connection'],
        improvements: ['Connect Google Search Console for accurate SEO scoring'],
        lastUpdated: new Date().toISOString(),
        dataQuality: 'poor'
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshScore = async () => {
    await fetchSEOScore();
  };

  useEffect(() => {
    if (enabled) {
      fetchSEOScore();
    }
  }, [websiteId, enabled]);

  return {
    seoScore,
    loading,
    error,
    refreshScore
  };
}


